import { useState, useRef, useCallback, useEffect } from "react";

interface UseVoiceInputOptions {
  onTranscript?: (text: string) => void;
  language?: string;
}

/** Minimal Web Speech API surface (Chromium `SpeechRecognition` / `webkitSpeechRecognition`). */
type WebSpeechRecognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onstart: (() => void) | null;
  onresult: ((ev: Event) => void) | null;
  onerror: ((ev: Event) => void) | null;
  onend: (() => void) | null;
};

/** Calm, user-facing copy for Web Speech API error codes. `null` = don't surface anything. */
function describeVoiceError(code: string): string | null {
  switch (code) {
    case "not-allowed":
    case "service-not-allowed":
      return "Microphone access isn't available. You can keep typing your response.";
    case "audio-capture":
      return "No microphone was found. You can keep typing your response.";
    case "network":
      return "Voice input needs a connection right now. You can keep typing your response.";
    case "no-speech":
      return "Didn't catch anything that time. Tap the mic to try again, or keep typing.";
    case "language-not-supported":
      return "Voice input isn't available for this language. You can keep typing your response.";
    case "aborted":
      return null;
    default:
      return "Voice input stopped unexpectedly. You can keep typing your response.";
  }
}

export function useVoiceInput({ onTranscript, language = "en-US" }: UseVoiceInputOptions = {}) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  /** Words recognized so far that the browser hasn't finalized yet. */
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const recognitionRef = useRef<WebSpeechRecognition | null>(null);
  const interimRef = useRef("");

  // Always call the latest callback, even though handlers are bound at start().
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const isSupported = typeof window !== "undefined" &&
    ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

  /** Detach and abort the active recognizer so no late result can fire. */
  const detach = useCallback(() => {
    const rec = recognitionRef.current;
    recognitionRef.current = null;
    if (!rec) return;
    rec.onstart = null;
    rec.onresult = null;
    rec.onerror = null;
    rec.onend = null;
    try {
      rec.abort();
    } catch {
      /* already stopped */
    }
  }, []);

  const setInterimText = (text: string) => {
    interimRef.current = text;
    setInterim(text);
  };

  const start = useCallback(() => {
    if (!isSupported) {
      setErrorCode("unsupported");
      setError("Voice input is not supported in this browser.");
      return;
    }
    if (recognitionRef.current) return;

    const SpeechRecognitionCtor = (
      (window as unknown as { SpeechRecognition?: new () => WebSpeechRecognition }).SpeechRecognition ??
      (window as unknown as { webkitSpeechRecognition?: new () => WebSpeechRecognition }).webkitSpeechRecognition
    )!;

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language;

    // Result indices already delivered as final, plus the last final text —
    // Android Chrome re-reports earlier finals (sometimes cumulatively) in continuous mode.
    const emitted = new Set<number>();
    let lastFinal = "";

    const emitFinal = (raw: string) => {
      let text = raw.trim();
      if (!text) return;
      if (lastFinal) {
        if (text === lastFinal) return;
        if (text.startsWith(lastFinal)) text = text.slice(lastFinal.length).trim();
        if (!text) return;
      }
      lastFinal = raw.trim();
      onTranscriptRef.current?.(text);
    };

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
      setErrorCode(null);
    };

    recognition.onresult = (event: Event) => {
      const ev = event as unknown as {
        resultIndex: number;
        results: SpeechRecognitionResultList;
      };
      let interimText = "";
      let finalText = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const result = ev.results[i];
        if (result.isFinal) {
          if (emitted.has(i)) continue;
          emitted.add(i);
          finalText += result[0].transcript;
          emitFinal(result[0].transcript);
        } else {
          interimText += result[0].transcript;
        }
      }
      setTranscript(finalText || interimText);
      setInterimText(interimText.trim());
    };

    recognition.onerror = (event: Event) => {
      const code = (event as unknown as { error?: string }).error ?? "unknown";
      const message = describeVoiceError(code);
      if (message) {
        setErrorCode(code);
        setError(message);
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      // Some browsers (notably iOS Safari) end without finalizing the last phrase.
      if (interimRef.current) emitFinal(interimRef.current);
      setInterimText("");
      setIsListening(false);
      if (recognitionRef.current === recognition) recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch {
      recognitionRef.current = null;
      setErrorCode("start-failed");
      setError(describeVoiceError("start-failed"));
    }
  }, [isSupported, language]);

  /** Graceful stop: the browser may still deliver the last final phrase. */
  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  /**
   * Hard stop for hand-offs (Continue, leaving a question): returns any
   * not-yet-final words so the caller can keep them, then aborts so no
   * late result can land anywhere else.
   */
  const flush = useCallback((): string => {
    const pending = interimRef.current;
    detach();
    setInterimText("");
    setIsListening(false);
    return pending;
  }, [detach]);

  const toggle = useCallback(() => {
    if (isListening) stop();
    else start();
  }, [isListening, start, stop]);

  const reset = useCallback(() => {
    detach();
    setIsListening(false);
    setTranscript("");
    setInterimText("");
    setError(null);
    setErrorCode(null);
  }, [detach]);

  const clearError = useCallback(() => {
    setError(null);
    setErrorCode(null);
  }, []);

  // Never leave the microphone running after the component goes away.
  useEffect(() => detach, [detach]);

  return {
    isListening,
    transcript,
    interim,
    error,
    errorCode,
    isSupported,
    start,
    stop,
    flush,
    toggle,
    reset,
    clearError,
  };
}
